import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DONE_STEP_ID,
  clearedHealthInfoZuidoFields,
  fetchHealthInfoZuidoProgress,
  upsertHealthInfoZuidoProgress,
} from './healthInfoZuidoApi';
import { isAgreeChoice, WORK_HISTORY_CORRECTION_CATEGORIES } from './healthInfoZuidoSteps';
import type {
  HealthInfoZuidoFormValues,
  HealthInfoZuidoStep,
  WorkHistoryCorrectionCategory,
  YesNo,
} from './healthInfoZuidoTypes';

export type HealthInfoZuidoCommittedEntry = {
  stepIndex: number;
  stepId: HealthInfoZuidoStep;
  displayText: string;
  rawValue: string;
};

type StepDef = {
  stepId: HealthInfoZuidoStep;
  fieldName: keyof HealthInfoZuidoFormValues;
  kind: 'intro' | 'choice' | 'category';
};

const CONSENT_CHOICE_STEPS: HealthInfoZuidoStep[] = ['agreeForm31', 'agreeForm32'];
const WORK_HISTORY_CHOICE_STEPS: HealthInfoZuidoStep[] = ['confirmWorkHistory', 'workHistoryCorrection'];
const NEXT_ASSISTANT_QUESTION_DELAY_MS = 520;

function formatFormAgreeLabel(t: (k: string) => string, raw: string): string {
  return isAgreeChoice(raw)
    ? t('submitDocuments.healthInfoZuido.iAgree')
    : t('submitDocuments.healthInfoZuido.iDoNotAgree');
}

function formatWorkHistoryConfirmLabel(t: (k: string) => string, raw: string): string {
  return isAgreeChoice(raw)
    ? t('submitDocuments.healthInfoZuido.workHistory.yesCorrect')
    : t('submitDocuments.healthInfoZuido.workHistory.noError');
}

function formatCorrectionCategoryLabel(
  t: (k: string) => string,
  category: WorkHistoryCorrectionCategory
): string {
  return t(`submitDocuments.healthInfoZuido.workHistory.correctionCategories.${category}`);
}

function isFormDeclined(values: HealthInfoZuidoFormValues): boolean {
  return values.agreeForm31 === 'no' || values.agreeForm32 === 'no';
}

function isWorkHistoryComplete(values: HealthInfoZuidoFormValues): boolean {
  if (values.workHistoryInfoCorrect === 'yes') return true;
  if (values.workHistoryInfoCorrect === 'no' && values.workHistoryCorrectionCategory) return true;
  return false;
}

function buildSteps(values: HealthInfoZuidoFormValues): StepDef[] {
  const steps: StepDef[] = [{ stepId: 'intro', fieldName: 'introContinued', kind: 'intro' }];
  if (values.introContinued !== 'true') {
    return steps;
  }
  steps.push({ stepId: 'agreeForm31', fieldName: 'agreeForm31', kind: 'choice' });
  if (values.agreeForm31 !== 'yes' && values.agreeForm31 !== 'no') {
    return steps;
  }
  if (values.agreeForm31 === 'no') {
    return steps;
  }
  steps.push({ stepId: 'agreeForm32', fieldName: 'agreeForm32', kind: 'choice' });
  if (values.agreeForm32 !== 'yes') {
    return steps;
  }
  if (values.forms312PreviewDone !== 'true') {
    return steps;
  }
  steps.push({ stepId: 'workHistoryIntro', fieldName: 'workHistoryIntroContinued', kind: 'intro' });
  if (values.workHistoryIntroContinued !== 'true') {
    return steps;
  }
  steps.push({ stepId: 'confirmWorkHistory', fieldName: 'workHistoryInfoCorrect', kind: 'choice' });
  if (values.workHistoryInfoCorrect !== 'yes' && values.workHistoryInfoCorrect !== 'no') {
    return steps;
  }
  if (values.workHistoryInfoCorrect === 'no') {
    steps.push({
      stepId: 'workHistoryCorrection',
      fieldName: 'workHistoryCorrectionCategory',
      kind: 'category',
    });
  }
  return steps;
}

function cursorStepIdForSave(steps: StepDef[], stepIndex: number): string | null {
  if (stepIndex >= steps.length) {
    const last = steps[steps.length - 1];
    if (last && isWorkHistoryCompleteFromSteps(steps)) {
      return DONE_STEP_ID;
    }
    if (last?.stepId === 'agreeForm32') {
      return 'agreeForm32';
    }
    return last?.stepId ?? DONE_STEP_ID;
  }
  return steps[stepIndex]?.stepId ?? null;
}

function isWorkHistoryCompleteFromSteps(steps: StepDef[]): boolean {
  const last = steps[steps.length - 1];
  return last?.stepId === 'confirmWorkHistory' || last?.stepId === 'workHistoryCorrection';
}

function upsertCommittedEntry(
  committed: HealthInfoZuidoCommittedEntry[],
  entry: HealthInfoZuidoCommittedEntry
): HealthInfoZuidoCommittedEntry[] {
  const next = [...committed];
  const idx = next.findIndex((c) => c.stepId === entry.stepId);
  if (idx >= 0) {
    next[idx] = entry;
    return next;
  }
  next.push(entry);
  return next;
}

/** Clear the undone step and every answer that depends on it. */
function clearFromStepInclusive(
  values: HealthInfoZuidoFormValues,
  undoFrom: HealthInfoZuidoStep
): HealthInfoZuidoFormValues {
  const next: HealthInfoZuidoFormValues = { ...values };

  const clearWorkHistoryAnswers = () => {
    delete next.workHistoryIntroContinued;
    delete next.workHistoryFormViewed;
    delete next.workHistoryInfoCorrect;
    delete next.workHistoryCorrectionCategory;
  };
  const clearAfterAgree32 = () => {
    delete next.forms312PreviewDone;
    clearWorkHistoryAnswers();
  };
  const clearAfterAgree31 = () => {
    delete next.agreeForm32;
    clearAfterAgree32();
  };

  switch (undoFrom) {
    case 'intro':
      delete next.introContinued;
      delete next.agreeForm31;
      clearAfterAgree31();
      break;
    case 'agreeForm31':
      delete next.agreeForm31;
      clearAfterAgree31();
      break;
    case 'agreeForm32':
      delete next.agreeForm32;
      clearAfterAgree32();
      break;
    case 'workHistoryIntro':
      clearWorkHistoryAnswers();
      break;
    case 'confirmWorkHistory':
      delete next.workHistoryInfoCorrect;
      delete next.workHistoryCorrectionCategory;
      break;
    case 'workHistoryCorrection':
      delete next.workHistoryCorrectionCategory;
      break;
    default:
      break;
  }
  return next;
}

function rebuildCommitted(values: HealthInfoZuidoFormValues, t: (k: string) => string): HealthInfoZuidoCommittedEntry[] {
  const committed: HealthInfoZuidoCommittedEntry[] = [];
  if (values.introContinued === 'true') {
    committed.push({
      stepIndex: 0,
      stepId: 'intro',
      rawValue: 'true',
      displayText: t('submitDocuments.chat.continue'),
    });
  }
  for (const stepId of CONSENT_CHOICE_STEPS) {
    const field = stepId === 'agreeForm31' ? 'agreeForm31' : 'agreeForm32';
    const raw = values[field];
    if (raw === 'yes' || raw === 'no') {
      committed.push({
        stepIndex: committed.length,
        stepId,
        rawValue: raw,
        displayText: formatFormAgreeLabel(t, raw),
      });
    }
    if (raw === 'no') {
      return committed;
    }
  }
  if (values.forms312PreviewDone !== 'true') {
    return committed;
  }
  if (values.workHistoryIntroContinued === 'true') {
    committed.push({
      stepIndex: committed.length,
      stepId: 'workHistoryIntro',
      rawValue: 'true',
      displayText: t('submitDocuments.chat.continue'),
    });
  }
  if (values.workHistoryInfoCorrect === 'yes' || values.workHistoryInfoCorrect === 'no') {
    committed.push({
      stepIndex: committed.length,
      stepId: 'confirmWorkHistory',
      rawValue: values.workHistoryInfoCorrect,
      displayText: formatWorkHistoryConfirmLabel(t, values.workHistoryInfoCorrect),
    });
  }
  if (values.workHistoryInfoCorrect === 'no' && values.workHistoryCorrectionCategory) {
    const cat = values.workHistoryCorrectionCategory as WorkHistoryCorrectionCategory;
    if (WORK_HISTORY_CORRECTION_CATEGORIES.includes(cat)) {
      committed.push({
        stepIndex: committed.length,
        stepId: 'workHistoryCorrection',
        rawValue: cat,
        displayText: formatCorrectionCategoryLabel(t, cat),
      });
    }
  }
  return committed;
}

function stepIndexFromProgress(
  values: HealthInfoZuidoFormValues,
  currentStepId: string | null | undefined,
  steps: StepDef[]
): number {
  if (isFormDeclined(values)) {
    return steps.length;
  }

  const forms312Complete =
    values.agreeForm31 === 'yes' &&
    values.agreeForm32 === 'yes' &&
    values.forms312PreviewDone !== 'true';

  if (forms312Complete) {
    return steps.length;
  }

  if (isWorkHistoryComplete(values)) {
    return steps.length;
  }

  const idx = steps.findIndex((s) => s.stepId === currentStepId);
  if (idx >= 0) {
    return idx;
  }

  let stepIndex = 0;
  if (values.introContinued === 'true') stepIndex = 1;
  if (values.agreeForm31 === 'yes' || values.agreeForm31 === 'no') {
    stepIndex = values.agreeForm31 === 'yes' ? 2 : steps.length;
  }
  if (values.agreeForm32 === 'yes' || values.agreeForm32 === 'no') {
    stepIndex = values.agreeForm32 === 'yes' ? 3 : steps.length;
  }
  if (values.forms312PreviewDone === 'true') {
    stepIndex = Math.max(stepIndex, 3);
    if (values.workHistoryIntroContinued === 'true') stepIndex = 4;
    if (values.workHistoryInfoCorrect === 'yes' || values.workHistoryInfoCorrect === 'no') {
      stepIndex = values.workHistoryInfoCorrect === 'no' && !values.workHistoryCorrectionCategory
        ? 5
        : steps.length;
    }
  }
  return stepIndex;
}

function rebuildFromProgress(
  values: HealthInfoZuidoFormValues,
  currentStepId: string | null | undefined,
  t: (k: string) => string
): {
  stepIndex: number;
  committed: HealthInfoZuidoCommittedEntry[];
  formDeclined: boolean;
  forms312Complete: boolean;
  workHistoryComplete: boolean;
  chatPhase: 'consent' | 'workHistory';
} {
  const committed = rebuildCommitted(values, t);
  const formDeclined = isFormDeclined(values);
  const steps = buildSteps(values);

  if (formDeclined) {
    return {
      stepIndex: steps.length,
      committed,
      formDeclined: true,
      forms312Complete: false,
      workHistoryComplete: false,
      chatPhase: 'consent',
    };
  }

  const forms312Complete =
    values.agreeForm31 === 'yes' &&
    values.agreeForm32 === 'yes' &&
    values.forms312PreviewDone !== 'true';

  const workHistoryComplete = isWorkHistoryComplete(values);
  const chatPhase = values.forms312PreviewDone === 'true' ? 'workHistory' : 'consent';

  const stepIndex = stepIndexFromProgress(values, currentStepId, steps);

  return {
    stepIndex,
    committed,
    formDeclined: false,
    forms312Complete,
    workHistoryComplete,
    chatPhase,
  };
}

export function useHealthInfoZuidoFlow(
  currentEmail: string | null | undefined,
  invitationId: string | null | undefined,
  t: (key: string) => string
) {
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [values, setValues] = useState<HealthInfoZuidoFormValues>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [committed, setCommitted] = useState<HealthInfoZuidoCommittedEntry[]>([]);
  const [formDeclined, setFormDeclined] = useState(false);
  const [forms312Complete, setForms312Complete] = useState(false);
  const [workHistoryComplete, setWorkHistoryComplete] = useState(false);
  const [chatPhase, setChatPhase] = useState<'consent' | 'workHistory'>('consent');
  const [assistantQuestionVisible, setAssistantQuestionVisible] = useState(true);
  const threadEndRef = useRef<HTMLDivElement | null>(null);

  const steps = useMemo(() => buildSteps(values), [values]);

  const currentStep = useMemo((): StepDef | null => {
    if (!steps.length) return null;
    if (stepIndex < steps.length) return steps[stepIndex];
    return null;
  }, [steps, stepIndex]);

  const reloadProgress = useCallback(async () => {
    const email = currentEmail?.trim();
    const inv = invitationId?.trim();
    if (!email || !inv) return;
    const payload = await fetchHealthInfoZuidoProgress(email, inv);
    const v = payload?.values ?? {};
    setValues(v);
    const rebuilt = rebuildFromProgress(v, payload?.currentStepId, t);
    setStepIndex(rebuilt.stepIndex);
    setCommitted(rebuilt.committed);
    setFormDeclined(rebuilt.formDeclined);
    setForms312Complete(rebuilt.forms312Complete);
    setWorkHistoryComplete(rebuilt.workHistoryComplete);
    setChatPhase(rebuilt.chatPhase);
  }, [currentEmail, invitationId, t]);

  useEffect(() => {
    let cancelled = false;
    const email = currentEmail?.trim();
    const inv = invitationId?.trim();
    if (!email || !inv) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void fetchHealthInfoZuidoProgress(email, inv).then((payload) => {
      if (cancelled) return;
      const v = payload?.values ?? {};
      setValues(v);
      const rebuilt = rebuildFromProgress(v, payload?.currentStepId, t);
      setStepIndex(rebuilt.stepIndex);
      setCommitted(rebuilt.committed);
      setFormDeclined(rebuilt.formDeclined);
      setForms312Complete(rebuilt.forms312Complete);
      setWorkHistoryComplete(rebuilt.workHistoryComplete);
      setChatPhase(rebuilt.chatPhase);
      setIsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [currentEmail, invitationId, t]);

  const persist = useCallback(
    async (
      nextValues: HealthInfoZuidoFormValues,
      nextStepIndex: number,
      stepsSnapshot?: StepDef[],
      clearFields?: ReadonlyArray<keyof HealthInfoZuidoFormValues>
    ) => {
      const email = currentEmail?.trim();
      const inv = invitationId?.trim();
      if (!email || !inv) return null;
      const nextSteps = stepsSnapshot ?? buildSteps(nextValues);
      const cursor = cursorStepIdForSave(nextSteps, nextStepIndex);
      setSaveError(null);
      const saved = await upsertHealthInfoZuidoProgress({
        email,
        invitationId: inv,
        values: nextValues,
        currentStepId: cursor,
        clearFields,
      });
      if (!saved) {
        setSaveError(t('submitDocuments.healthInfoZuido.saveFailed'));
        return null;
      }
      return saved;
    },
    [currentEmail, invitationId, t]
  );

  const revealNextQuestion = useCallback(() => {
    setAssistantQuestionVisible(false);
    window.setTimeout(() => setAssistantQuestionVisible(true), NEXT_ASSISTANT_QUESTION_DELAY_MS);
  }, []);

  const markConsentRead = useCallback(async () => {
    const nextValues = { ...values, consentDocumentRead: 'true' };
    const saved = await persist(nextValues, stepIndex);
    if (!saved) return false;
    setValues(nextValues);
    return true;
  }, [values, persist, stepIndex]);

  const markWorkHistoryFormViewed = useCallback(async () => {
    const nextValues = { ...values, workHistoryFormViewed: 'true' };
    const saved = await persist(nextValues, stepIndex);
    if (!saved) return false;
    setValues(nextValues);
    return true;
  }, [values, persist, stepIndex]);

  const advanceIntro = useCallback(async () => {
    const entry: HealthInfoZuidoCommittedEntry = {
      stepIndex: 0,
      stepId: 'intro',
      rawValue: 'true',
      displayText: t('submitDocuments.chat.continue'),
    };
    const nextValues = { ...values, introContinued: 'true' };
    const nextSteps = buildSteps(nextValues);
    const saved = await persist(nextValues, 1, nextSteps);
    if (!saved) return;
    setValues(nextValues);
    setCommitted([entry]);
    setStepIndex(1);
    revealNextQuestion();
  }, [values, persist, t, revealNextQuestion]);

  const advanceFormAgree = useCallback(
    async (stepId: 'agreeForm31' | 'agreeForm32', choice: YesNo) => {
      if (!choice) return;
      const fieldName = stepId === 'agreeForm31' ? 'agreeForm31' : 'agreeForm32';
      const stepOrdinal = stepId === 'agreeForm31' ? 1 : 2;
      const entry: HealthInfoZuidoCommittedEntry = {
        stepIndex: stepOrdinal,
        stepId,
        rawValue: choice,
        displayText: formatFormAgreeLabel(t, choice),
      };
      const nextValues = { ...values, [fieldName]: choice };

      if (choice === 'no') {
        const nextSteps = buildSteps(nextValues);
        const saved = await persist(nextValues, nextSteps.length, nextSteps);
        if (!saved) return;
        setValues(nextValues);
        setCommitted((prev) => upsertCommittedEntry(prev, entry));
        setFormDeclined(true);
        setStepIndex(nextSteps.length);
        return;
      }

      if (stepId === 'agreeForm32') {
        const nextSteps = buildSteps(nextValues);
        const saved = await persist(nextValues, nextSteps.length, nextSteps);
        if (!saved) return;
        setValues(nextValues);
        setCommitted((prev) => upsertCommittedEntry(prev, entry));
        setForms312Complete(true);
        setStepIndex(nextSteps.length);
        revealNextQuestion();
        return;
      }

      const nextSteps = buildSteps(nextValues);
      const saved = await persist(nextValues, 2, nextSteps);
      if (!saved) return;
      setValues(nextValues);
      setCommitted((prev) => upsertCommittedEntry(prev, entry));
      setStepIndex(2);
      revealNextQuestion();
    },
    [values, persist, t, revealNextQuestion]
  );

  const advanceAfterForms312Preview = useCallback(async () => {
    const nextValues = { ...values, forms312PreviewDone: 'true' };
    const nextSteps = buildSteps(nextValues);
    const workHistoryStepIndex = nextSteps.findIndex((s) => s.stepId === 'workHistoryIntro');
    const saved = await persist(nextValues, workHistoryStepIndex >= 0 ? workHistoryStepIndex : nextSteps.length, nextSteps);
    if (!saved) return;
    setValues(nextValues);
    setForms312Complete(false);
    setChatPhase('workHistory');
    setStepIndex(workHistoryStepIndex >= 0 ? workHistoryStepIndex : nextSteps.length);
    revealNextQuestion();
  }, [values, persist, revealNextQuestion]);

  const advanceWorkHistoryIntro = useCallback(async () => {
    const entry: HealthInfoZuidoCommittedEntry = {
      stepIndex: committed.length,
      stepId: 'workHistoryIntro',
      rawValue: 'true',
      displayText: t('submitDocuments.chat.continue'),
    };
    const nextValues = { ...values, workHistoryIntroContinued: 'true' };
    const nextSteps = buildSteps(nextValues);
    const confirmIdx = nextSteps.findIndex((s) => s.stepId === 'confirmWorkHistory');
    const saved = await persist(nextValues, confirmIdx >= 0 ? confirmIdx : nextSteps.length, nextSteps);
    if (!saved) return;
    setValues(nextValues);
    setCommitted((prev) => upsertCommittedEntry(prev, entry));
    setStepIndex(confirmIdx >= 0 ? confirmIdx : nextSteps.length);
    revealNextQuestion();
  }, [values, committed.length, persist, t, revealNextQuestion]);

  const advanceConfirmWorkHistory = useCallback(
    async (choice: YesNo) => {
      if (!choice) return;
      const entry: HealthInfoZuidoCommittedEntry = {
        stepIndex: committed.length,
        stepId: 'confirmWorkHistory',
        rawValue: choice,
        displayText: formatWorkHistoryConfirmLabel(t, choice),
      };
      const nextValues = { ...values, workHistoryInfoCorrect: choice };
      const nextSteps = buildSteps(nextValues);

      if (choice === 'yes') {
        const saved = await persist(nextValues, nextSteps.length, nextSteps);
        if (!saved) return;
        setValues(nextValues);
        setCommitted((prev) => upsertCommittedEntry(prev, entry));
        setWorkHistoryComplete(true);
        setStepIndex(nextSteps.length);
        return;
      }

      const correctionIdx = nextSteps.findIndex((s) => s.stepId === 'workHistoryCorrection');
      const saved = await persist(nextValues, correctionIdx >= 0 ? correctionIdx : nextSteps.length, nextSteps);
      if (!saved) return;
      setValues(nextValues);
      setCommitted((prev) => upsertCommittedEntry(prev, entry));
      setStepIndex(correctionIdx >= 0 ? correctionIdx : nextSteps.length);
      revealNextQuestion();
    },
    [values, committed.length, persist, t, revealNextQuestion]
  );

  const advanceWorkHistoryCorrection = useCallback(
    async (category: WorkHistoryCorrectionCategory) => {
      const entry: HealthInfoZuidoCommittedEntry = {
        stepIndex: committed.length,
        stepId: 'workHistoryCorrection',
        rawValue: category,
        displayText: formatCorrectionCategoryLabel(t, category),
      };
      const nextValues = { ...values, workHistoryCorrectionCategory: category };
      const nextSteps = buildSteps(nextValues);
      const saved = await persist(nextValues, nextSteps.length, nextSteps);
      if (!saved) return;
      setValues(nextValues);
      setCommitted((prev) => upsertCommittedEntry(prev, entry));
      setWorkHistoryComplete(true);
      setStepIndex(nextSteps.length);
    },
    [values, committed.length, persist, t]
  );

  const handleUndo = useCallback(
    async (undoFromStep?: HealthInfoZuidoStep) => {
      if (committed.length === 0) return;
      const undoFrom =
        undoFromStep ??
        committed[committed.length - 1]?.stepId;
      if (!undoFrom) return;
      if (!committed.some((c) => c.stepId === undoFrom)) return;

      const nextValues = clearFromStepInclusive(values, undoFrom);
      const clearFields = clearedHealthInfoZuidoFields(values, nextValues);
      const nextSteps = buildSteps(nextValues);
      const landOnIndex = nextSteps.findIndex((s) => s.stepId === undoFrom);
      const stepIdx = landOnIndex >= 0 ? landOnIndex : 0;
      const saved = await persist(nextValues, stepIdx, nextSteps, clearFields);
      if (!saved) return;
      const rebuilt = rebuildFromProgress(nextValues, undoFrom, t);
      setValues(nextValues);
      setCommitted(rebuilt.committed);
      setStepIndex(rebuilt.stepIndex);
      setFormDeclined(rebuilt.formDeclined);
      setForms312Complete(rebuilt.forms312Complete);
      setWorkHistoryComplete(rebuilt.workHistoryComplete);
      setChatPhase(rebuilt.chatPhase);
      setAssistantQuestionVisible(true);
    },
    [committed, values, persist, t]
  );

  return {
    t,
    isLoading,
    saveError,
    values,
    committed,
    formDeclined,
    forms312Complete,
    workHistoryComplete,
    chatPhase,
    currentStep,
    assistantQuestionVisible,
    undoLabel: t('submitDocuments.chat.undo'),
    canShowUndo: committed.length > 0,
    handleUndo,
    markConsentRead,
    markWorkHistoryFormViewed,
    advanceIntro,
    advanceFormAgree,
    advanceAfterForms312Preview,
    advanceWorkHistoryIntro,
    advanceConfirmWorkHistory,
    advanceWorkHistoryCorrection,
    reloadProgress,
    threadEndRef,
  };
}
